import { mapTokensToVariables, TokenMapper, VariableMap } from '../../core/token-mapping';
import { pseudoStateVariable } from '../../core/tokens';
import { PStyle, PseudoTokenMap, TokenCategory } from '../../core/types';
import { duration, timing } from '../../extractors/animations';
import { color, foregroundColorFor } from '../../extractors/colors';
import { component } from '../../extractors/components';
import { shadow } from '../../extractors/shadows';
import { radius, size, spacing } from '../../extractors/sizes';
import {
    createTypographyTokenExtractor,
    fontFamily,
    fontSize,
    fontStyle,
    fontWeight,
    letterSpacing,
    lineHeight,
    textDecoration,
    textTransform,
    weight
} from '../../extractors/typography';
import { AnimationDurationName, AnimationTimingName } from '../../values/animations';
import { ColorName } from '../../values/colors';
import { ComponentValueToken } from '../../values/components';
import { ShadowName } from '../../values/shadows';
import { RadiusName, SizeName, SpacingName } from '../../values/sizes';
import { TypographyProperty, TypographyVariantName, TypographyWeightName } from '../../values/typography';

/**
 * Maps typography properties to their respective token extractors.
 * Each extractor converts a typography variant name to its corresponding CSS variable.
 */
const typographyMappers: Record<TypographyProperty, ReturnType<typeof createTypographyTokenExtractor>> = {
    'font-size': fontSize,
    'font-family': fontFamily,
    'font-weight': fontWeight,
    'font-style': fontStyle,
    'line-height': lineHeight,
    'letter-spacing': letterSpacing,
    'text-decoration': textDecoration,
    'text-transform': textTransform
};

/**
 * Maps token categories to their respective token mappers.
 * Each mapper converts a token value to its corresponding CSS variable.
 */
const tokenMappers: Record<TokenCategory, TokenMapper<any>> = {
    color: (token: ColorName) => color(token),
    'color-foreground': (token: ColorName) => foregroundColorFor(token),
    typography: (token: TypographyVariantName, property) => typographyMappers[property as TypographyProperty](token),
    'typography-weight': (token: TypographyWeightName) => weight(token),
    size: (token: SizeName) => size(token),
    spacing: (token: SpacingName) => spacing(token),
    radius: (token: RadiusName) => radius(token),
    shadow: (token: ShadowName) => shadow(token),
    'animation-duration': (token: AnimationDurationName) => duration(token),
    'animation-timing': (token: AnimationTimingName) => timing(token),
    component: (token: ComponentValueToken) => component(...token),
    pseudo: () => {
        throw new Error('Pseudo category should be handled by mapPseudoStyles function');
    }
};

/**
 * Maps pseudo state styles to CSS custom properties with pseudo prefixes.
 * Reuses the existing mapStyles function to handle all token types within each pseudo state.
 *
 * @param pseudoTokenMap Object mapping pseudo states to their PStyle configurations
 * @returns Map of CSS custom properties with pseudo prefixes (e.g., --pseudo-hover-color)
 *
 * @example
 * ```typescript
 * const pseudoStyles = {
 *   hover: {
 *     color: { color: 'logo-light' },
 *     typography: { 'font-size': 'heading-1' }
 *   },
 *   focus: {
 *     color: { color: 'primary' }
 *   }
 * };
 *
 * const result = mapPseudoStyles(pseudoStyles);
 * // Result: {
 * //   '--pseudo-hover-color': 'var(--p-color-logo-light)',
 * //   '--pseudo-hover-font-size': 'var(--font-size-heading-1)',
 * //   '--pseudo-focus-color': 'var(--color-primary)'
 * // }
 * ```
 */
export function mapPseudoStyles(pseudoTokenMap: PseudoTokenMap): VariableMap {
    return Object.entries(pseudoTokenMap).reduce<VariableMap>((acc, [state, pStyle]) => {
        // Reuse existing mapStyles function to handle all token types
        const mappedStyles = mapStyles(pStyle);

        // Transform CSS properties to pseudo-prefixed custom properties
        const pseudoPrefixed = Object.entries(mappedStyles).reduce(
            (styles, [property, value]) => ({
                ...styles,
                [pseudoStateVariable(state, property)]: value
            }),
            {}
        );

        return { ...acc, ...pseudoPrefixed };
    }, {});
}

/**
 * Combines multiple token mappers into a single mapper function.
 * Takes a PStyle object and returns a map of CSS properties to their CSS variable values.
 *
 * @param pStyle Object containing token maps for different categories
 * @returns Map of CSS properties to CSS variables
 */
export function mapStyles(pStyle: PStyle): VariableMap {
    const { pseudo, ...regularStyles } = pStyle;

    // Map regular styles generically with improved typing
    const regularMapped = Object.entries(regularStyles).reduce<VariableMap>((styles, [category, tokens]) => {
        const mapper = tokenMappers[category as TokenCategory];
        // Type assertion is safe here because we know the category matches the token type at runtime
        return { ...styles, ...mapTokensToVariables(tokens as Parameters<typeof mapper>[0], mapper) };
    }, {});

    // Map pseudo styles if present
    const pseudoMapped = pseudo ? mapPseudoStyles(pseudo) : {};

    // Combine both
    return { ...regularMapped, ...pseudoMapped };
}
